import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { filter, map, take } from 'rxjs/operators';

import { Order } from 'src/app/domain/Order';
import { GlobalParams, GlobalService } from 'src/app/services/global.service';
import { CustomerService } from 'src/app/services/customer.service';
import { OrderService } from 'src/app/services/order.service';
import { ProductService } from './product.service';
import { StorageService } from './storage.service';
import { CountryService } from 'src/app/services/country.service';
import { GTMService } from './gtm/gtm.service';

import { ecommerceConstants } from 'src/app/shared/ecommerce-constants';
import { baseJsonLdCountry } from 'src/app/shared/base-json-ld-country';

import { environment } from 'src/environments/environment';
import { Product } from '../domain/Product';
import { Md5 } from 'ts-md5';

// Hotjar declarations
declare const hj: any;

interface HotjarWindow extends Window {
  hj?: any;
  _hjSettings?: { hjid: number; hjsv: number };
}

type BreadcrumbUI = { label: string; url?: string };

@Injectable({
  providedIn: 'root',
})
export class SEOService {
  private readonly document = inject(DOCUMENT);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly translateService = inject(TranslateService);
  globalService = inject(GlobalService);
  private readonly customerService = inject(CustomerService);
  private readonly orderService = inject(OrderService);
  private readonly productService = inject(ProductService);
  private readonly storageService = inject(StorageService);
  private readonly countryService = inject(CountryService);
  private readonly gtmService = inject(GTMService);

  updateLang(locale: string) {
    this.document.documentElement.lang = locale;
  }

  updateMeta(translationKey?: string, params?: any, force?: { metaTitle: string; metaDescription: string }) {
    const applyParams = (txt?: string) =>
      txt && params ? txt.replace(/\{\{\s*(\w+)\s*\}\}/g, (_m, k) => params[k] ?? '') : txt ?? '';

    if (!force) {
      this.translateService.get(`${translationKey}.METATITLE`, params).subscribe((translatedTitle: string) => {
        this.title.setTitle(`${translatedTitle}`);
      });
      this.translateService
        .get(`${translationKey}.METADESCRIPTION`, params)
        .subscribe((translatedDescription: string) => {
          this.meta.updateTag({ name: 'description', content: `${translatedDescription}` });
        });
    } else {
      this.title.setTitle(applyParams(force.metaTitle));
      this.meta.updateTag({ name: 'description', content: applyParams(force.metaDescription) });
    }
  }

  updateTitle(title: string, translatableTitle: boolean = true, params?: any) {
    if (translatableTitle) {
      this.translateService.get(title, params).subscribe((translatedTitle: string) => {
        this.title.setTitle(`${translatedTitle}`);
      });
    } else {
      this.title.setTitle(`${title}`);
    }
  }

  updateThumbnail(image: string) {
    this.meta.updateTag({ name: 'thumbnail', content: image });
  }

  addNoIndex() {
    this.meta.updateTag({ name: 'robots', content: 'noindex' });
  }

  removeNoIndex() {
    this.meta.removeTag('name="robots"');
  }

  updateCanonicalUrl(key?: string, extra?: string) {
    const head = this.document.getElementsByTagName('head')[0];
    let element: HTMLLinkElement = this.document.querySelector(`link[rel='canonical']`) || null;

    if (!element) {
      element = this.document.createElement('link') as HTMLLinkElement;
      element.setAttribute('rel', 'canonical');
      head.appendChild(element);
    }

    // Root
    if (!key) {
      element.setAttribute('href', `${environment.url}/`);
      return;
    }

    // Pages with country and language
    const currentParams = this.globalService.currentParams;
    const countryCode = currentParams?.country?.code;
    const locale = currentParams?.country?.locale;

    if (!countryCode || !locale) {
      element.setAttribute('href', `${environment.url}${this.document.location.pathname}`);
      return;
    }

    let url = `${environment.url}/${countryCode}/${locale}/${ecommerceConstants.urls[key][countryCode]}`;
    if (extra) url += `/${extra}`;
    element.setAttribute('href', url);
  }

  loadHreflang(slug: string) {
    const head = this.document.getElementsByTagName('head')[0];

    Array.from(this.document.head.querySelectorAll('link')).forEach((link: any) => {
      if (link.hasAttribute('hreflang')) {
        link.remove();
      }
    });

    this.countryService.availableCountries.forEach((country) => {
      if (!ecommerceConstants.urls[slug] || !country.code) {
        return;
      }
      const alternateUrl = ecommerceConstants.urls[slug][country.code];
      if (alternateUrl) {
        const linkElt = this.document.createElement('link');
        linkElt.setAttribute('rel', 'alternate');
        if (['ME'].includes(country.code)) {
          linkElt.setAttribute('hreflang', 'sr-me');
        } else {
          linkElt.setAttribute('hreflang', `${country.locale}-${country.code.toLowerCase()}`);
        }
        linkElt.setAttribute('href', `${environment.url}/${country.code}/${country.locale}/${alternateUrl}`);
        head.appendChild(linkElt);
      }
    });
    // x-default
    const defaultCountry = this.countryService.getCountryByCode('ES');
    if (defaultCountry?.code) {
      const alternateUrl = ecommerceConstants.urls[slug][defaultCountry.code];
      if (alternateUrl) {
        const linkElt = this.document.createElement('link');
        linkElt.setAttribute('rel', 'alternate');
        linkElt.setAttribute('hreflang', 'x-default');
        linkElt.setAttribute(
          'href',
          `${environment.url}/${defaultCountry.code}/${defaultCountry.locale}/${alternateUrl}`
        );
        head.appendChild(linkElt);
      }
    }
  }

  loadHreflangFromApi(hreflangs: any) {
    const params = this.globalService.currentParams;
    const head = this.document.getElementsByTagName('head')[0];

    Array.from(this.document.head.querySelectorAll('link')).forEach((link: any) => {
      if (link.hasAttribute('hreflang')) {
        link.remove();
      }
    });

    hreflangs.forEach((hreflang: any) => {
      const linkElt = this.document.createElement('link');
      linkElt.setAttribute('rel', 'alternate');
      if (['ME'].includes(hreflang.countryCode)) {
        linkElt.setAttribute('hreflang', 'sr-me');
      } else {
        linkElt.setAttribute('hreflang', `${hreflang.locale}-${hreflang.countryCode.toLowerCase()}`);
      }
      linkElt.setAttribute(
        'href',
        `${environment.url}/${hreflang.countryCode}/${hreflang.locale}/${hreflang.slug}`
      );
      head.appendChild(linkElt);
    });
    // x-default
    let defaultHreflang: any;
    defaultHreflang = hreflangs.find((x: any) => x.countryCode === 'ES');
    if (!defaultHreflang) {
      defaultHreflang = hreflangs.find((x: any) => x.countryCode === params.country.code);
    }
    if (!defaultHreflang) {
      return;
    }
    const linkElt = this.document.createElement('link');
    linkElt.setAttribute('rel', 'alternate');
    linkElt.setAttribute('hreflang', 'x-default');
    linkElt.setAttribute(
      'href',
      `${environment.url}/${defaultHreflang.countryCode}/${defaultHreflang.locale}/${defaultHreflang.slug}`
    );
    head.appendChild(linkElt);
  }

  loadProductMicrodata(product: Product, params: GlobalParams, breadcrumbs?: BreadcrumbUI[]) {
    if (!product || !params) {
      return;
    }

    const image = [product.mainImageUrl];
    if (product.galleryImageUrls?.length) {
      product.galleryImageUrls.forEach((g) => image.push(g.mediumUrl));
    }

    let finalPrice = product.isMaster ? product.minPrice : product.price;
    finalPrice = this.roundPrice(finalPrice * product.packUnits);

    const currentOrder = this.orderService.currentOrder;
    const shippingCost = currentOrder ? this.roundPrice(currentOrder.order.shippingCostWithTaxes) : null;

    const productSchema = {
      '@type': 'Product',
      name: product.title,
      image,
      description: product.description,
      sku: product.sku,
      offers: {
        '@type': 'Offer',
        priceCurrency: params?.country?.currency,
        price: finalPrice,
        priceValidUntil: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        availability:
          product.notAvailable || this.productService.noStockToBuy(product, params?.country?.securityStock)
            ? 'https://schema.org/OutOfStock'
            : 'https://schema.org/InStock',
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingDestination: {
            '@type': 'DefinedRegion',
            addressCountry: params?.country?.code,
          },
          deliveryTime: {
            '@type': 'ShippingDeliveryTime',
            businessDays: {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: [
                'https://schema.org/Monday',
                'https://schema.org/Tuesday',
                'https://schema.org/Wednesday',
                'https://schema.org/Thursday',
                'https://schema.org/Friday',
              ],
              opens: '09:00',
              closes: '17:00',
            },
            handlingTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 1, unitCode: 'DAY' },
            transitTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 7, unitCode: 'DAY' },
          },
          shippingRate: {
            '@type': 'MonetaryAmount',
            value: shippingCost !== null ? `<?= number_format(${shippingCost}, 2, ".", "") ?>` : null,
            currency: params?.country?.currency,
          },
        },
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
          merchantReturnDays: 30,
          returnMethod: 'https://schema.org/ReturnByMail',
          returnFees: 'https://schema.org/FreeReturn',
          applicableCountry: params?.country?.code,
        },
      },
    };

    const nodes: any[] = [productSchema];

    if (breadcrumbs?.length) {
      nodes.unshift(this.buildBreadcrumbJsonLd(params, breadcrumbs));
    }

    this.loadSchemaJsonLd(params?.country?.code, nodes);
  }

  // DATALAYER

  sendPageView(pageType: string) {

    const loggedCustomer = this.customerService.loggedCustomer;
    const currentParams = this.globalService.currentParams;

    if (typeof window === 'undefined') {
      return;
    }

    // Sanitize URL to remove sensitive tokens (password reset, confirmation, etc.)
    const rawPageURL = window.location.href;
    const pageURL = this.gtmService.sanitizeUrl(rawPageURL);

    const pageHostname = `${window.location.origin}/${currentParams.country.code}/${currentParams.country.locale}`;
    const pagePath = pageURL.replace(pageHostname, '');

    this.gtmService.pushPageView({
      pageURL,
      pageHostname,
      pagePath,
      pageType,
      userLogged: loggedCustomer ? 's' : 'n',
      userId: loggedCustomer ? loggedCustomer.id : undefined,
      hashedEmail: loggedCustomer ? Md5.hashStr(loggedCustomer.emailLogin) : undefined,
      tipoCliente: loggedCustomer ? (loggedCustomer.role === 1 ? 'external' : 'partner') : undefined,
    });
  }

  sendGenericEvent(eventCategory: string, eventAction: string, eventLabel: string) {

    const loggedCustomer = this.customerService.loggedCustomer;

    this.gtmService.pushGenericEvent({
      userId: loggedCustomer ? loggedCustomer.id : undefined,
      eventCategory,
      eventAction,
      eventLabel,
    });
  }

  sendPromoImpressions(name: string, promotions: any[], position: string) {

    if (!promotions) {
      return;
    }

    const loggedCustomer = this.customerService.loggedCustomer;

    const promotionsArray = promotions.map((promo, i) => ({
      id: promo?.id,
      name,
      creative: promo?.title,
      position: position + '_' + (promo?.position ? promo?.position : i + 1),
    }));

    this.gtmService.pushPromoImpressions({
      userId: loggedCustomer ? loggedCustomer.id : undefined,
      ecommerce: {
        promoView: {
          promotions: promotionsArray,
        },
      },
    });
  }

  sendPromoClick(id: any, name: string, creative: string, position: string, positionNumber: number) {

    const loggedCustomer = this.customerService.loggedCustomer;

    const promotionArray = [{
      id,
      name,
      creative,
      position: position + '_' + (positionNumber + 1),
    }];

    this.gtmService.pushPromoClick({
      userId: loggedCustomer ? loggedCustomer.id : undefined,
      ecommerce: {
        promoClick: {
          promotions: promotionArray,
        },
      },
    });
  }

  sendProductImpressions(list: string, products: Product[]) {

    const currentParams = this.globalService.currentParams;
    const loggedCustomer = this.customerService.loggedCustomer;

    const productsArray = products.map((product, i) => {
      let category: string | undefined;

      if (product.categories) {
        category = product.categories
          .map((productCategory: any) => productCategory.title)
          .join(';');
      }

      return {
        id: product.id,
        name: product.title,
        list,
        category,
        position: i + 1,
        price: this.roundPrice(product.price * product.packUnits),
        quantity: 1,
        dimension5: 'sin descuento',
        dimension6: undefined,
        dimension7: 'producto',
        item_category2: product.datalayerCategory,
        metric1: this.roundPrice(product.price * product.packUnits),
      };
    });

    this.gtmService.pushProductImpressions({
      userId: loggedCustomer ? loggedCustomer.id : undefined,
      ecommerce: {
        currencyCode: currentParams?.country?.currency,
        impressions: productsArray,
      },
    });
  }

  sendProductClick(list: string, product: Product, position?: number) {

    const loggedCustomer = this.customerService.loggedCustomer;

    let category: string | undefined;

    if (product.categories) {
      category = product.categories
        .map((productCategory: any) => productCategory.title)
        .join(';');
    }

    this.gtmService.pushProductClick({
      userId: loggedCustomer ? loggedCustomer.id : undefined,
      ecommerce: {
        click: {
          actionField: { list },
          products: [
            {
              id: product.id,
              name: product.title,
              list,
              category,
              position: position !== undefined ? position + 1 : undefined,
              price: this.roundPrice(product.price * product.packUnits),
              quantity: 1,
              dimension5: 'sin descuento',
              dimension6: undefined,
              dimension7: 'producto',
              item_category2: product.datalayerCategory,
              metric1: this.roundPrice(product.price * product.packUnits),
            },
          ],
        },
      },
    });
  }

  sendServiceImpressions(list: string, services: any[], position: string) {

    const currentParams = this.globalService.currentParams;
    const loggedCustomer = this.customerService.loggedCustomer;

    const servicesArray = services
      .filter(service => service)
      .map((service, i) => ({
        id: service.id,
        name: service.title ? service.title : service.link,
        list,
        category: undefined,
        position: position + '_' + (service.position ? service.position : i + 1),
        price: undefined,
        quantity: 1,
        dimension5: 'sin descuento',
        dimension6: undefined,
        dimension7: 'servicio',
        item_category2: undefined,
        metric1: undefined,
      }));

    this.gtmService.pushProductImpressions({
      userId: loggedCustomer ? loggedCustomer.id : undefined,
      ecommerce: {
        currencyCode: currentParams?.country?.currency,
        impressions: servicesArray,
      },
    });
  }

  sendServiceClick(list: string, service: any, position?: number) {

    const loggedCustomer = this.customerService.loggedCustomer;

    this.gtmService.pushProductClick({
      userId: loggedCustomer ? loggedCustomer.id : undefined,
      ecommerce: {
        click: {
          actionField: { list },
          products: [
            {
              id: service.link,
              name: service.title,
              list,
              category: undefined,
              position: position !== undefined ? position + 1 : undefined,
              price: undefined,
              quantity: 1,
              dimension5: 'sin descuento',
              dimension6: undefined,
              dimension7: 'servicio',
              item_category2: undefined,
              metric1: undefined,
            },
          ],
        },
      },
    });
  }

  sendPostClick(list: string, post: any, position?: number) {

    const loggedCustomer = this.customerService.loggedCustomer;

    this.gtmService.pushProductClick({
      userId: loggedCustomer ? loggedCustomer.id : undefined,
      ecommerce: {
        click: {
          actionField: { list },
          products: [
            {
              id: post.id,
              name: post.title,
              list,
              category: undefined,
              position: position !== undefined ? position + 1 : undefined,
              price: undefined,
              quantity: 1,
              dimension5: 'sin descuento',
              dimension6: undefined,
              dimension7: 'post',
              item_category2: undefined,
              metric1: undefined,
            },
          ],
        },
      },
    });
  }

  sendAddProductToCart(list: string, product: Product, quantity: number, position?: number) {

    const currentParams = this.globalService.currentParams;
    const loggedCustomer = this.customerService.loggedCustomer;

    let category: string | undefined;

    if (product.categories) {
      category = product.categories
        .map((productCategory: any) => productCategory.title)
        .join(';');
    }

    const galleryImageUrls = product.galleryImageUrls
      ? product.galleryImageUrls.map((galleryImage) => galleryImage.mediumUrl)
      : [];

    const countryCode = currentParams ? currentParams.country.code : null;
    const locale = currentParams ? currentParams.country.locale : null;
    const url = `${environment.url}/${countryCode}/${locale}/${ecommerceConstants.urls.product[countryCode]}/${product.slug}-${product.sku}`;

    this.gtmService.pushAddToCart({
      userId: loggedCustomer ? loggedCustomer.id : undefined,
      ecommerce: {
        currencyCode: currentParams?.country?.currency,
        add: {
          products: [
            {
              id: product.id,
              name: product.title,
              image: product.mainImageMediumUrl,
              images: [product.mainImageMediumUrl, ...galleryImageUrls],
              url,
              list,
              category,
              position: position !== undefined ? position + 1 : undefined,
              price: this.roundPrice(product.price * product.packUnits),
              quantity: quantity / product.packUnits,
              dimension5: 'sin descuento',
              dimension6: undefined,
              dimension7: 'producto',
              item_category2: product.datalayerCategory,
              metric1: this.roundPrice(product.price * product.packUnits),
            },
          ],
        },
      },
    });
  }

  sendProductDetail(product: Product) {

    const currentParams = this.globalService.currentParams;
    const loggedCustomer = this.customerService.loggedCustomer;

    let category: string | undefined;

    const allCategories = product.categories ?? [];
    if (
      product.mainCategory &&
      allCategories.filter((c: any) => c.title === product.mainCategory.title && c.slug === product.mainCategory.slug)
        .length <= 0
    ) {
      allCategories.push(product.mainCategory);
    }
    if (allCategories.length > 0) {
      category = allCategories
        .map((productCategory: any) => productCategory.title)
        .join(';');
    }

    const galleryImageUrls = product.galleryImageUrls
      ? product.galleryImageUrls.map((galleryImage) => galleryImage.mediumUrl)
      : [];

    const countryCode = currentParams ? currentParams.country.code : null;
    const locale = currentParams ? currentParams.country.locale : null;
    const url = `${environment.url}/${countryCode}/${locale}/${ecommerceConstants.urls.product[countryCode]}/${product.slug}-${product.sku}`;

    this.gtmService.pushProductDetail({
      userId: loggedCustomer ? loggedCustomer.id : undefined,
      ecommerce: {
        currencyCode: currentParams?.country?.currency,
        detail: {
          products: [
            {
              id: product.id,
              name: product.title,
              image: product.mainImageMediumUrl,
              images: [product.mainImageMediumUrl, ...galleryImageUrls],
              url,
              list: undefined,
              category,
              position: undefined,
              price: this.roundPrice(product.price * product.packUnits),
              quantity: 1,
              dimension5: 'sin descuento',
              dimension6: undefined,
              dimension7: 'producto',
              item_category2: product.datalayerCategory,
              metric1: this.roundPrice(product.price * product.packUnits),
            },
          ],
        },
      },
    });
  }

  sendRemoveProductFromCart(list: string, product: Product, quantity: number, position?: number) {

    const currentParams = this.globalService.currentParams;
    const loggedCustomer = this.customerService.loggedCustomer;

    let category: string | undefined;

    if (product.categories) {
      category = product.categories
        .map((productCategory: any) => productCategory.title)
        .join(';');
    }

    this.gtmService.pushRemoveFromCart({
      userId: loggedCustomer ? loggedCustomer.id : undefined,
      ecommerce: {
        currencyCode: currentParams?.country?.currency,
        remove: {
          products: [
            {
              id: product.id,
              name: product.title,
              list,
              category,
              position: position !== undefined ? position + 1 : undefined,
              price: this.roundPrice(product.price * product.packUnits),
              quantity: quantity / product.packUnits,
              dimension5: 'sin descuento',
              dimension6: undefined,
              dimension7: 'producto',
              item_category2: product.datalayerCategory,
              metric1: this.roundPrice(product.price * product.packUnits),
            },
          ],
        },
      },
    });
  }

  sendCheckoutStepOne(products: Product[]) {
    this.sendCheckoutStep(1, products);
  }

  sendCheckoutStepTwo(products: Product[]) {
    this.sendCheckoutStep(2, products);
  }

  private sendCheckoutStep(step: number, products: Product[]) {

    const currentParams = this.globalService.currentParams;
    const loggedCustomer = this.customerService.loggedCustomer;
    const currentOrder = this.orderService.currentOrder;

    const productsArray = products.map((product, i) => {
      let category: string | undefined;

      if (product.categories) {
        category = product.categories
          .map((productCategory: any) => productCategory.title)
          .join(';');
      }

      return {
        id: product.id,
        name: product.title,
        list: undefined,
        category,
        position: i + 1,
        price: this.roundPrice(product.price * product.packUnits),
        quantity: product.qt / product.packUnits,
        dimension5: 'sin descuento',
        dimension6: undefined,
        dimension7: 'producto',
        item_category2: product.datalayerCategory,
        metric1: this.roundPrice(product.price * product.packUnits),
      };
    });

    this.gtmService.pushCheckout({
      userId: loggedCustomer ? loggedCustomer.id : undefined,
      ecommerce: {
        currencyCode: currentParams?.country?.currency,
        checkout: {
          actionField: { step },
          products: productsArray,
          orderCosts: {
            revenue: currentOrder ? currentOrder.order.totalCost : null,
            tax: currentOrder ? currentOrder.order.taxCost : null,
            shipping: currentOrder ? currentOrder.order.shippingCostWithTaxes : null,
          },
        },
      },
    });
  }

  sendCheckoutOptionStepTwo(option: string) {
    this.sendCheckoutOption(2, option);
  }

  sendCheckoutStepThree(products: Product[]) {
    this.sendCheckoutStep(3, products);
  }

  sendCheckoutStepFour(option: string) {
    this.sendCheckoutOption(4, option);
  }

  private sendCheckoutOption(step: number, option: string) {

    const loggedCustomer = this.customerService.loggedCustomer;
    const currentOrder = this.orderService.currentOrder;

    this.gtmService.pushCheckoutOption({
      userId: loggedCustomer ? loggedCustomer.id : undefined,
      ecommerce: {
        checkout_option: {
          actionField: { step, option },
          orderCosts: {
            revenue: currentOrder ? currentOrder.order.totalCost : null,
            tax: currentOrder ? currentOrder.order.taxCost : null,
            shipping: currentOrder ? currentOrder.order.shippingCostWithTaxes : null,
          },
        },
      },
    });
  }

  sendOrderConfirmation() {

    const currentParams = this.globalService.currentParams;
    const loggedCustomer = this.customerService.loggedCustomer;

    const order = this.storageService.getItem<Order>('orderSEO');

    if (!order) {
      return;
    }

    const productsArray = order.products.map((product, i) => {
      let category: string | undefined;

      if (product.categories) {
        category = product.categories
          .map((productCategory: any) => productCategory.title)
          .join(';');
      }

      return {
        id: product.id,
        name: product.title,
        list: undefined,
        category,
        position: i + 1,
        price: this.roundPrice(product.price * product.packUnits),
        quantity: product.qt / product.packUnits,
        dimension5: 'sin descuento',
        dimension6: undefined,
        dimension7: 'producto',
        item_category2: product.datalayerCategory,
        metric1: this.roundPrice(product.price * product.packUnits),
      };
    });

    this.gtmService.pushPurchase({
      userId: loggedCustomer ? loggedCustomer.id : undefined,
      ecommerce: {
        currencyCode: currentParams?.country?.currency,
        purchase: {
          actionField: {
            id: order.order.id,
            revenue: order.order.totalCost,
            tax: order.order.taxCost,
            shipping: order.order.shippingCostWithTaxes,
            coupon: undefined,
          },
          products: productsArray,
        },
      },
    });

    this.storageService.removeItem('orderSEO');
  }

  roundPrice(price: number) {
    return Math.round((price + Number.EPSILON) * 100) / 100;
  }

  loadHotjar() {
    (function (h: HotjarWindow, o, t, j, a, r) {
      h.hj =
        h.hj ||
        function () {
          (h.hj.q = h.hj.q || []).push(arguments);
        };
      h._hjSettings = { hjid: environment.hotjar.id, hjsv: 6 };
      a = o.getElementsByTagName('head')[0];
      r = o.createElement('script');
      r.async = 1;
      r.src = t + h._hjSettings!.hjid + j + h._hjSettings!.hjsv;
      a.appendChild(r);
    })(window as HotjarWindow, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');
  }

  /**
   * Hotjar will register these JavaScript events as page changes
   * @param url
   */
  trackHotjarUrl(url: string) {
    if (typeof hj !== 'undefined') {
      hj('stateChange', url);
    }
  }

  loadSchemaJsonLd(countryCode: string, extraNodes: any[] = []) {
    const schemaElement = this.document.getElementById('schema-json-ld');
    if (!schemaElement) {
      return;
    }

    const el = schemaElement as HTMLScriptElement;
    const raw = (el.text || el.textContent || '').trim();
    if (!raw) {
      return;
    }

    const content = JSON.parse(raw);
    content['@graph'] = [];

    const shopCfg = this.globalService.getShoptConfiguration();
    const brandName = shopCfg?.name ?? '';
    const brandLogo = shopCfg?.logoColorDesktop?.url ?? '';

    if (!brandName) {
      this.globalService.params$
        .pipe(
          map((p) => p?.shopConfiguration),
          filter(Boolean),
          take(1)
        )
        .subscribe(() => this.loadSchemaJsonLd(countryCode, extraNodes));
      return;
    }

    if (baseJsonLdCountry[countryCode]) {
      baseJsonLdCountry[countryCode].forEach((baseContentElement) => {
        const branded = this.applyBrandToSchemaNode(baseContentElement, brandName, brandLogo);
        content['@graph'].push(branded);
      });
    }

    extraNodes
      .filter(Boolean)
      .forEach((node) => content['@graph'].push(node));

    el.text = JSON.stringify(content);
  }

  private applyBrandToSchemaNode(node: any, brandName: string, brandLogo?: string): any {
    const copy = JSON.parse(JSON.stringify(node));

    const replacePlaceholders = (value: any): any => {
      if (typeof value === 'string') {
        return value
          .replace(/__BRAND_NAME__/g, brandName)
          .replace(/__BRAND_LOGO__/g, brandLogo ?? '')
          .replace(/__BASE_URL__/g, environment.url);
      }
      if (Array.isArray(value)) {
        return value.map((v) => replacePlaceholders(v));
      }
      if (typeof value === 'object' && value !== null) {
        return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, replacePlaceholders(v)]));
      }
      return value;
    };

    return replacePlaceholders(copy);
  }

  private buildBreadcrumbJsonLd(params: GlobalParams, breadcrumbs: BreadcrumbUI[]) {
    const countryCode = params?.country?.code;
    const locale = params?.country?.locale;

    // 1) Home
    const homePath =
      (countryCode && ecommerceConstants.urls.home?.[countryCode]) ??
      ecommerceConstants.urls.home?.ES ?? 'home';

    const homeUrl = `${environment.url}/${countryCode}/${locale}/${homePath}`;
    const t = (key: string, fallback: string) => {
      const v = this.translateService.instant(key);
      return v && v !== key ? v : fallback;
    };
    const homeName = t('COMMON.BREADCRUMBS.HOME', locale === 'es' ? 'Inicio' : 'Home');

    // 2) Normaliza: quita posibles items sin url y evita duplicados por url
    const normalized = breadcrumbs
      .filter(b => !!b?.label)
      .map(b => ({
        name: b.label,
        // IMPORTANTE: Google prefiere absolute URLs en "item"
        item: b.url ? (b.url.startsWith('http') ? b.url : `${environment.url}${b.url}`) : null,
      }))
      .filter(b => !!b.item);

    // 3) Elimina "Catálogo de productos" si existe (porque tu posición 1 debe ser Home)
    const catalogPath = countryCode ? ecommerceConstants.urls['product-catalog']?.[countryCode] : null;
    const catalogUrl = catalogPath ? `${environment.url}/${countryCode}/${locale}/${catalogPath}` : null;

    const filtered = normalized.filter(b => {
      if (!catalogUrl) return true;
      return b.item !== catalogUrl;
    });

    // 4) Compón lista final con Home siempre al inicio
    const finalList = [
      { name: homeName, item: homeUrl },
      ...filtered,
    ].filter((v, i, arr) => arr.findIndex(x => x.item === v.item) === i);

    return {
      '@type': 'BreadcrumbList',
      itemListElement: finalList.map((b, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: b.name,
        item: b.item,
      })),
    };
  }
}
